import { useEffect, useRef } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  canonicalUrl: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  articlePublishedTime?: string;
  articleAuthor?: string;
  articleTags?: string[];
  jsonLdScripts?: Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  canonicalUrl,
  keywords,
  ogType = 'website',
  ogImage,
  ogTitle,
  ogDescription,
  articlePublishedTime,
  articleAuthor,
  articleTags,
  jsonLdScripts = [],
}: PageMetaProps) {
  const scriptRefs = useRef<HTMLScriptElement[]>([]);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) canonicalEl.setAttribute('href', canonicalUrl);
    else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalUrl);
      document.head.appendChild(link);
    }

    setMeta('og:type', ogType, true);
    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:url', canonicalUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime, true);
    if (articleAuthor) setMeta('article:author', articleAuthor, true);
    if (articleTags) {
      articleTags.forEach((tag) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'article:tag');
        el.setAttribute('content', tag);
        document.head.appendChild(el);
      });
    }

    jsonLdScripts.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      scriptRefs.current.push(script);
    });

    return () => {
      scriptRefs.current.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
      scriptRefs.current = [];
    };
  }, []);

  return null;
}